/** @jsx jsx */
import type { InputHTMLAttributes, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { jsx } from '@balance-web/core';
import { VisuallyHidden } from '@balance-web/a11y';
import { useTheme } from '@balance-web/theme';

import { GUTTER } from './constants';
import type { SegmentedControlSizeType } from './types';

type SegmentProps = {
  children: ReactNode;
  checked: boolean;
  /**
   * Not `size`: that name is already taken on an input element, and the
   * intersection with InputHTMLAttributes would collapse it to `never`.
   */
  controlSize: SegmentedControlSizeType;
} & InputHTMLAttributes<HTMLInputElement>;
export const Segment = forwardRef<HTMLLabelElement, SegmentProps>(
  ({ children, controlSize, ...props }, ref) => {
    const { semantic, radii, spacing } = useTheme();

    return (
      <label
        ref={ref}
        css={{
          alignItems: 'center',
          justifyContent: 'center',
          appearance: 'none',
          backgroundClip: 'padding-box',
          borderRadius: `calc(${radii.small} - ${GUTTER})`,
          boxSizing: 'border-box',
          color: semantic.colour.neutral.container.foreground,
          cursor: 'pointer',
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          flex: 1,
          outline: 0,
          paddingBottom: 0,
          paddingLeft:
            controlSize === 'xsmall' ? spacing.small : spacing.medium,
          paddingRight:
            controlSize === 'xsmall' ? spacing.small : spacing.medium,
          paddingTop: 0,
          position: 'relative',
          userSelect: 'none',
          zIndex: 2,
          '&:hover': {
            color: semantic.colour.text.textPrimary,
          },
          '&:active': {
            color: semantic.colour.text.textPrimary,
          },
          '&[data-checked="true"]': {
            color: semantic.colour.text.textPrimary,
          },
        }}
        data-checked={props.checked}
      >
        <VisuallyHidden as="input" type="radio" {...props} />
        {children}
      </label>
    );
  }
);
Segment.displayName = 'SegmentedControlSegment';
